/**
 * FounderDashboardExtended — Phase 12 / M10 advanced dashboard.
 *
 * Composition:
 *   ┌──────────────────────────────────────┐
 *   │ Cross-project KPIs                   │
 *   ├──────────────────┬───────────────────┤
 *   │ Needs attention   │ System incidents │
 *   ├──────────────────┴───────────────────┤
 *   │ Department health (full width)        │
 *   ├──────────────────┬───────────────────┤
 *   │ Active sessions   │ Recent decisions │
 *   └──────────────────┴───────────────────┘
 */
import * as React from 'react';

import { cn } from '../../lib/utils';
import { ActiveSessions } from './active-sessions';
import { CrossProjectKPIs } from './cross-project-kpis';
import {
  useCrossProjectKpis,
  useDepartmentHealth,
  useNeedsAttention,
  useRecentDecisions,
  useSystemIncidents,
} from './dashboard-api';
import { DepartmentHealthGrid } from './department-health-grid';
import { NeedsAttention } from './needs-attention';
import { RecentDecisions } from './recent-decisions';
import { SystemIncidents } from './system-incidents';

export interface FounderDashboardExtendedProps {
  onOpenCard?: (cardId: string, boardId?: string) => void;
  onOpenDepartment?: (departmentId: string) => void;
  className?: string;
}

export function FounderDashboardExtended({
  onOpenCard,
  onOpenDepartment,
  className,
}: FounderDashboardExtendedProps) {
  const [kpiWindow, setKpiWindow] = React.useState<'7d' | '30d' | '90d'>('30d');

  const kpis = useCrossProjectKpis(kpiWindow);
  const deptHealth = useDepartmentHealth();
  const incidents = useSystemIncidents();
  const decisions = useRecentDecisions(20);
  const needsAttention = useNeedsAttention();

  return (
    <div className={cn('flex flex-col gap-4 p-6', className)}>
      <header className="flex items-baseline justify-between">
        <h1 className="text-lg font-semibold text-foreground">Founder dashboard</h1>
        <span className="text-2xs text-muted-foreground">
          Refreshes every 10–120s depending on tile.
        </span>
      </header>

      <CrossProjectKPIs data={kpis.data} isLoading={kpis.isLoading} onChangeWindow={setKpiWindow} />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <NeedsAttention
          data={needsAttention.data}
          isLoading={needsAttention.isLoading}
          onOpenCard={onOpenCard}
        />
        <SystemIncidents data={incidents.data} isLoading={incidents.isLoading} />
      </div>

      <DepartmentHealthGrid
        departments={deptHealth.data?.departments ?? []}
        isLoading={deptHealth.isLoading}
        onOpenDepartment={onOpenDepartment}
      />

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ActiveSessions onOpenCard={onOpenCard} />
        <RecentDecisions
          data={decisions.data}
          isLoading={decisions.isLoading}
          onOpenCard={onOpenCard}
        />
      </div>
    </div>
  );
}
